import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  FaUniversalAccess,
  FaTimes,
  FaUndo,
  FaVolumeUp,
  FaVolumeMute,
} from "react-icons/fa";
import "./Accessibility.css";

const STORAGE_KEY = "zetech_a11y_prefs";
const ZOOM_STEPS = [100, 110, 125, 140]; // percent — see the zoom-vs-transform note below
const SPACING_STEPS = [0, 1, 2, 3]; // px of extra letter-spacing
const LINE_HEIGHT_STEPS = [0, 1.6, 1.9, 2.2]; // 0 = untouched

const ZOOM_DESC = {
  label: "Text size",
  value: (i) => `${ZOOM_STEPS[i]} percent`,
};
const SPACING_DESC = {
  label: "Text spacing",
  value: (i) => (i === 0 ? "normal" : `plus ${SPACING_STEPS[i]} pixels`),
};
const LINE_HEIGHT_DESC = {
  label: "Line height",
  value: (i) => (i === 0 ? "normal" : `${LINE_HEIGHT_STEPS[i]} times`),
};

const DEFAULT_PREFS = {
  zoomIndex: 0,
  spacingIndex: 0,
  lineHeightIndex: 0,
  highContrast: false,
  invertColours: false,
  greyHues: false,
  underlineLinks: false,
  bigCursor: false,
  readingGuide: false,
  highlightFocus: false,
  dyslexiaFont: false,
  reduceMotion: false,
  readAloud: false,
};

function loadPrefs() {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_PREFS;
    return { ...DEFAULT_PREFS, ...JSON.parse(raw) };
  } catch {
    return DEFAULT_PREFS;
  }
}

function savePrefs(prefs) {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
  } catch {
    // Storage unavailable (private mode etc) — non-fatal, just won't persist.
  }
}

/**
 * Floating accessibility toolbar: text size, text spacing, line height,
 * contrast modes, link underlining, big cursor, reading guide, focus
 * highlighting, dyslexia-friendly font, reduced motion and read-aloud —
 * all applied globally via classes (and inline styles for the numeric
 * settings) on <html>, and remembered across visits via localStorage.
 *
 * Text size uses the CSS `zoom` property rather than `transform:
 * scale()`. This codebase's CSS is almost entirely px-based (not
 * rem), so a root font-size change would do very little — `zoom`
 * is the one mechanism that reliably scales a legacy px-heavy layout
 * without breaking this site's fixed-position header/floats the way
 * `transform: scale()` would.
 */
export default function AccessibilityWidget() {
  const [open, setOpen] = useState(false);
  const [prefs, setPrefs] = useState(loadPrefs);
  const [speaking, setSpeaking] = useState(false);
  const [announcement, setAnnouncement] = useState("");

  const rootRef = useRef(null);
  const panelRef = useRef(null);
  const toggleBtnRef = useRef(null);

  /* ---------- apply preferences to <html> ---------- */
  useEffect(() => {
    const html = document.documentElement;
    html.style.zoom = `${ZOOM_STEPS[prefs.zoomIndex]}%`;
    html.style.setProperty("--a11y-letter-spacing", `${SPACING_STEPS[prefs.spacingIndex]}px`);
    html.style.setProperty(
      "--a11y-line-height",
      LINE_HEIGHT_STEPS[prefs.lineHeightIndex] ? String(LINE_HEIGHT_STEPS[prefs.lineHeightIndex]) : ""
    );

    html.classList.toggle("a11y-spacing", prefs.spacingIndex > 0);
    html.classList.toggle("a11y-line-height", prefs.lineHeightIndex > 0);
    html.classList.toggle("a11y-high-contrast", prefs.highContrast);
    html.classList.toggle("a11y-invert", prefs.invertColours);
    html.classList.toggle("a11y-grey", prefs.greyHues);
    html.classList.toggle("a11y-underline-links", prefs.underlineLinks);
    html.classList.toggle("a11y-big-cursor", prefs.bigCursor);
    html.classList.toggle("a11y-highlight-focus", prefs.highlightFocus);
    html.classList.toggle("a11y-dyslexia", prefs.dyslexiaFont);
    html.classList.toggle("a11y-reduce-motion", prefs.reduceMotion);
    savePrefs(prefs);
  }, [prefs]);

  /* ---------- reading guide ---------- */
  useEffect(() => {
    if (!prefs.readingGuide) return undefined;

    const guide = document.createElement("div");
    guide.className = "a11y-reading-guide";
    guide.setAttribute("aria-hidden", "true");
    document.body.appendChild(guide);

    const move = (e) => {
      const y = e.touches?.[0]?.clientY ?? e.clientY;
      if (typeof y === "number") guide.style.top = `${y}px`;
    };

    window.addEventListener("mousemove", move);
    window.addEventListener("touchmove", move, { passive: true });

    return () => {
      window.removeEventListener("mousemove", move);
      window.removeEventListener("touchmove", move);
      guide.remove();
    };
  }, [prefs.readingGuide]);

  /* ---------- read aloud (text to speech) ---------- */
  const speech = typeof window !== "undefined" ? window.speechSynthesis : undefined;

  const stopSpeaking = useCallback(() => {
    speech?.cancel();
    setSpeaking(false);
  }, [speech]);

  useEffect(() => {
    if (!prefs.readAloud || !speech) return undefined;

    const speak = (text) => {
      if (!text || !text.trim()) return;
      speech.cancel();
      const utterance = new window.SpeechSynthesisUtterance(text.trim().slice(0, 800));
      utterance.lang = document.documentElement.lang || "en";
      utterance.onend = () => setSpeaking(false);
      utterance.onerror = () => setSpeaking(false);
      setSpeaking(true);
      speech.speak(utterance);
    };

    const onPointer = (e) => {
      if (rootRef.current?.contains(e.target)) return;
      const selected = window.getSelection?.().toString();
      const el = e.target.closest?.(
        "p,h1,h2,h3,h4,h5,h6,li,a,button,span,td,th,label,figcaption,blockquote"
      );
      speak(selected || el?.innerText || "");
    };

    const onFocusIn = (e) => {
      if (rootRef.current?.contains(e.target)) return;
      const el = e.target;
      speak(el.getAttribute?.("aria-label") || el.innerText || "");
    };

    document.addEventListener("click", onPointer, true);
    document.addEventListener("focusin", onFocusIn);

    return () => {
      document.removeEventListener("click", onPointer, true);
      document.removeEventListener("focusin", onFocusIn);
      speech.cancel();
      setSpeaking(false);
    };
  }, [prefs.readAloud, speech]);

  /* ---------- open/close behavior: outside click, Escape, focus trap ---------- */
  useEffect(() => {
    if (!open) return undefined;

    const handleClickOutside = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };

    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        setOpen(false);
        toggleBtnRef.current?.focus();
        return;
      }

      if (e.key !== "Tab" || !panelRef.current) return;

      const focusables = panelRef.current.querySelectorAll(
        'button:not([disabled]),input:not([disabled]),select,a[href],[tabindex]:not([tabindex="-1"])'
      );
      if (!focusables.length) return;

      const first = focusables[0];
      const last = focusables[focusables.length - 1];

      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKeyDown);

    // Move focus into the panel for screen-reader / keyboard users.
    const timer = window.setTimeout(() => {
      panelRef.current?.querySelector("button, input")?.focus();
    }, 0);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
      window.clearTimeout(timer);
    };
  }, [open]);

  /* ---------- keep the desktop panel inside the viewport ---------- */
  useEffect(() => {
    if (!open) return undefined;

    const fit = () => {
      const panel = panelRef.current;
      const btn = toggleBtnRef.current;
      if (!panel || !btn) return;

      if (window.innerWidth <= 600) {
        panel.style.maxHeight = "";
        return;
      }

      const gap = 16;
      const available = btn.getBoundingClientRect().top - gap * 2;
      panel.style.maxHeight = `${Math.max(240, Math.min(620, available))}px`;
    };

    fit();
    window.addEventListener("resize", fit);
    return () => window.removeEventListener("resize", fit);
  }, [open]);

  /* ---------- lock the page + hide other floats while the sheet is open ---------- */
  useEffect(() => {
    document.body.classList.toggle("a11y-open", open);
    return () => document.body.classList.remove("a11y-open");
  }, [open]);

  /* ---------- global shortcut: Alt + A ---------- */
  useEffect(() => {
    const onKey = (e) => {
      if (e.altKey && (e.key === "a" || e.key === "A")) {
        e.preventDefault();
        setOpen((v) => !v);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  /* ---------- live announcements for screen readers ---------- */
  const announce = useCallback((message) => {
    // Re-set through an empty string so repeated identical messages
    // (e.g. two "Text size 125%" in a row) are still spoken.
    setAnnouncement("");
    window.setTimeout(() => setAnnouncement(message), 60);
  }, []);

  const step = (key, steps, dir, describe) =>
    setPrefs((p) => {
      const next = Math.min(steps.length - 1, Math.max(0, p[key] + dir));
      if (next === p[key]) {
        announce(`${describe.label} is already at the ${dir > 0 ? "maximum" : "minimum"}`);
        return p;
      }
      announce(`${describe.label} ${describe.value(next)}`);
      return { ...p, [key]: next };
    });

  const toggle = (key, label) =>
    setPrefs((p) => {
      const next = !p[key];
      announce(`${label} ${next ? "on" : "off"}`);
      return { ...p, [key]: next };
    });

  const reset = () => {
    stopSpeaking();
    setPrefs(DEFAULT_PREFS);
    announce("All accessibility settings reset to their defaults");
  };


  const groups = useMemo(
    () => [
      {
        title: "Color & contrast",
        items: [
          { key: "highContrast", label: "High contrast" },
          { key: "invertColours", label: "Invert colors" },
          { key: "greyHues", label: "Grey hues" },
        ],
      },
      {
        title: "Reading aids",
        items: [
          { key: "underlineLinks", label: "Underline links" },
          { key: "bigCursor", label: "Big cursor" },
          { key: "readingGuide", label: "Reading guide" },
          { key: "highlightFocus", label: "Highlight focus" },
          { key: "dyslexiaFont", label: "Readable font" },
        ],
      },
      {
        title: "Motion & audio",
        items: [
          { key: "reduceMotion", label: "Reduce motion" },
          { key: "readAloud", label: "Read aloud", hint: "Click any text to hear it" },
        ],
      },
    ],
    []
  );

  const activeCount = Object.keys(DEFAULT_PREFS).filter((key) =>
    typeof DEFAULT_PREFS[key] === "boolean" ? prefs[key] : prefs[key] > 0
  ).length;


  return (
    <div className="a11y-widget notranslate" translate="no" ref={rootRef}>
      <button
        type="button"
        ref={toggleBtnRef}
        className="a11y-toggle-btn"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls="a11y-panel"
        aria-label="Accessibility options (Alt + A)"
        title="Accessibility options (Alt + A)"
      >
        <span className="a11y-toggle-tooltip" aria-hidden="true">
          Accessibility
        </span>
        <FaUniversalAccess aria-hidden="true" />
        {activeCount > 0 && (
          <span className="a11y-badge" aria-hidden="true">
            {activeCount}
          </span>
        )}
      </button>

      {open && (
        <>
          <div
            className="a11y-backdrop"
            aria-hidden="true"
            onClick={() => setOpen(false)}
          />

          <div
            className="a11y-panel"
            id="a11y-panel"
            ref={panelRef}
            role="dialog"
            aria-modal="false"
            aria-labelledby="a11y-panel-title"
          >
            <div className="a11y-panel-head">
              <div>
                <h3 id="a11y-panel-title">Accessibility</h3>
                <p className="a11y-panel-sub">
                  {activeCount > 0
                    ? `${activeCount} setting${activeCount > 1 ? "s" : ""} active`
                    : "Adjust the page to suit you"}
                </p>
              </div>
              <button
                type="button"
                className="a11y-close"
                onClick={() => {
                  setOpen(false);
                  toggleBtnRef.current?.focus();
                }}
                aria-label="Close accessibility options"
              >
                <FaTimes aria-hidden="true" />
              </button>
            </div>

            <div className="a11y-panel-body">
              <section className="a11y-group" aria-labelledby="a11y-grp-text">
                <h4 className="a11y-group-title" id="a11y-grp-text">
                  Text
                </h4>

                <div className="a11y-row">
                  <span id="a11y-size-label">Text size</span>
                  <div className="a11y-zoom-controls" role="group" aria-labelledby="a11y-size-label">
                    <button
                      type="button"
                      onClick={() => step("zoomIndex", ZOOM_STEPS, -1, ZOOM_DESC)}
                      disabled={prefs.zoomIndex === 0}
                      aria-label="Decrease text size"
                    >
                      A−
                    </button>
                    <span className="a11y-zoom-value">
                      {ZOOM_STEPS[prefs.zoomIndex]}%
                    </span>
                    <button
                      type="button"
                      onClick={() => step("zoomIndex", ZOOM_STEPS, 1, ZOOM_DESC)}
                      disabled={prefs.zoomIndex === ZOOM_STEPS.length - 1}
                      aria-label="Increase text size"
                    >
                      A+
                    </button>
                  </div>
                </div>

                <div className="a11y-row">
                  <span id="a11y-spacing-label">Text spacing</span>
                  <div className="a11y-zoom-controls" role="group" aria-labelledby="a11y-spacing-label">
                    <button
                      type="button"
                      onClick={() => step("spacingIndex", SPACING_STEPS, -1, SPACING_DESC)}
                      disabled={prefs.spacingIndex === 0}
                      aria-label="Decrease text spacing"
                    >
                      −
                    </button>
                    <span className="a11y-zoom-value">
                      {prefs.spacingIndex === 0
                        ? "Normal"
                        : `+${SPACING_STEPS[prefs.spacingIndex]}px`}
                    </span>
                    <button
                      type="button"
                      onClick={() => step("spacingIndex", SPACING_STEPS, 1, SPACING_DESC)}
                      disabled={prefs.spacingIndex === SPACING_STEPS.length - 1}
                      aria-label="Increase text spacing"
                    >
                      +
                    </button>
                  </div>
                </div>

                <div className="a11y-row">
                  <span id="a11y-lh-label">Line height</span>
                  <div className="a11y-zoom-controls" role="group" aria-labelledby="a11y-lh-label">
                    <button
                      type="button"
                      onClick={() => step("lineHeightIndex", LINE_HEIGHT_STEPS, -1, LINE_HEIGHT_DESC)}
                      disabled={prefs.lineHeightIndex === 0}
                      aria-label="Decrease line height"
                    >
                      −
                    </button>
                    <span className="a11y-zoom-value">
                      {prefs.lineHeightIndex === 0
                        ? "Normal"
                        : `${LINE_HEIGHT_STEPS[prefs.lineHeightIndex]}×`}
                    </span>
                    <button
                      type="button"
                      onClick={() => step("lineHeightIndex", LINE_HEIGHT_STEPS, 1, LINE_HEIGHT_DESC)}
                      disabled={prefs.lineHeightIndex === LINE_HEIGHT_STEPS.length - 1}
                      aria-label="Increase line height"
                    >
                      +
                    </button>
                  </div>
                </div>
              </section>

              {groups.map((group) => (
                <section
                  className="a11y-group"
                  key={group.title}
                  aria-labelledby={`a11y-grp-${group.title.replace(/\W+/g, "-")}`}
                >
                  <h4
                    className="a11y-group-title"
                    id={`a11y-grp-${group.title.replace(/\W+/g, "-")}`}
                  >
                    {group.title}
                  </h4>

                  {group.items.map(({ key, label, hint }) => (
                    <label className="a11y-toggle-row" key={key}>
                      <span className="a11y-toggle-label">
                        {label}
                        {hint && <small>{hint}</small>}
                      </span>
                      <input
                        type="checkbox"
                        checked={prefs[key]}
                        onChange={() => {
                          if (key === "readAloud" && prefs.readAloud) stopSpeaking();
                          toggle(key, label);
                        }}
                      />
                      <span className="a11y-switch" aria-hidden="true" />
                    </label>
                  ))}

                  {group.title === "Motion & audio" && prefs.readAloud && (
                    <button
                      type="button"
                      className="a11y-speak-btn"
                      onClick={stopSpeaking}
                      aria-label="Stop reading aloud"
                    >
                      {speaking ? (
                        <FaVolumeUp aria-hidden="true" />
                      ) : (
                        <FaVolumeMute aria-hidden="true" />
                      )}
                      {speaking ? "Stop speaking" : "Not speaking"}
                    </button>
                  )}
                </section>
              ))}
            </div>

            <div className="a11y-panel-foot">
              <button type="button" className="a11y-reset" onClick={reset}>
                <FaUndo aria-hidden="true" /> Reset all
              </button>
              <p className="a11y-hint">
                Press <kbd>Alt</kbd> + <kbd>A</kbd> any time
              </p>
            </div>
          </div>
        </>
      )}


      <span className="a11y-sr-only" role="status" aria-live="polite" aria-atomic="true">
        {announcement}
      </span>

      <span className="a11y-sr-only" role="status" aria-live="polite">
        {speaking ? "Reading page text aloud" : ""}
      </span>
    </div>
  );
}
